import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import { colors, borderRadius } from '../../constants/styleConstants';
import { useTranslation } from '../../src/i18n';
import { styles } from './FormikAutocompleteSelect';

const MuiPhoneNumber = dynamic(import('material-ui-phone-number'), { ssr: false });

export const usePhoneFieldStyles = makeStyles(() => ({
  field: {
    height: '100%',
    width: '100%',
    paddingTop: 16,
    position: 'relative',
    '& .MuiFormControl-root': {
      width: '100%',
      height: '100%',
    },
    '& .MuiInputBase-root': {
      width: '100%',
      height: '100%',
      paddingTop: 0,
      border: `1px solid ${colors.fieldsBorderGray}`,
      borderRadius: borderRadius.fields,
      backgroundColor: colors.white,
      '& input': {
        padding: '8px 0',
        height: 26,
        '&:focus': {
          backgroundColor: colors.background.gray,
          outline: 'none',
        },
      },
      '&:after': {
        content: 'none',
        borderBottom: 0,
      },
      '&:before': {
        content: 'none',
      },
    },
  },
  error: {
    '& .MuiInputBase-root': {
      border: `1px solid ${colors.red}`,
    },
  },
  label: {
    position: 'absolute',
    left: 0,
    top: 0,
    color: colors.title,
    letterSpacing: 0.6,
    ...styles.shrink,
  },
  disabled: {
    userSelect: 'none',
  },
  asterisk: {
    color: colors.primary,
    lineHeight: '1em',
  },
}));

const PhoneField = ({ value, onChange, hasError, errorText, required, disabled, className, label }) => {
  const classes = usePhoneFieldStyles();
  const { t } = useTranslation();
  const [defaultCountry] = useState('gb');

  // TODO check ru case, maybe change the library
  // useEffect(() => {
  //   if (i18n.language !== 'en' && !value) {
  //     setDefaultCountry(i18n.language);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <div className={classnames(classes.field, className, { [classes.disabled]: disabled, [classes.error]: hasError })}>
      {label && (
        <label className={classes.label}>
          {label}
          {required && <span className={classes.asterisk}>*</span>}
        </label>
      )}
      <MuiPhoneNumber
        required={required}
        value={value}
        defaultCountry={defaultCountry}
        onChange={(newValue, country) => onChange(newValue, country)}
        isValid={() => !hasError}
        error={hasError}
        helperText={(hasError && t(errorText)) || ''}
        inputProps={{
          disabled,
        }}
      />
    </div>
  );
};

PhoneField.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
  hasError: PropTypes.bool,
  errorText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

PhoneField.defaultProps = {
  onChange: () => {},
  value: '',
  hasError: false,
  errorText: '',
  required: false,
  disabled: false,
  className: '',
};

export default memo(PhoneField);
